import { useCallback } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { supabase } from "@/integrations/supabase/client";
import { FreelancerListItem } from "@/types/freelancer";
import {
  FreelancerSortOption,
  FreelancerFilterValues,
} from "@/hooks/use-freelancers-filter-sort";

const EMPTY: FreelancerListItem[] = [];

// Colunas leves o suficiente para resolver freelancers por id (abrir links,
// comparar) sem puxar os campos pesados de texto.
const BY_IDS_COLUMNS =
  "id,nome,email,telefone,cidade,city,state,country,areas,tipo_atuacao,idiomas," +
  "pretensao_salarial,quality_seal,falar_com_frila,is_active,linkedin_url,portfolio_url," +
  "last_submitted_at,created_at,media_avaliacao_projeto,media_avaliacao_perfil," +
  "maior_nota_geral,total_avaliacoes_projeto,total_avaliacoes_perfil,atributos_medias";

interface UseFreelancersDataParams {
  searchTerm: string;
  searchOnlyName: boolean;
  filters: FreelancerFilterValues;
  sortOption: FreelancerSortOption;
  page: number;
  pageSize: number;
}

export interface FreelancersDataResult {
  /** Itens da página atual. */
  pageItems: FreelancerListItem[];
  /** Total de freelancers cadastrados (todos). */
  totalCount: number;
  /** Total após aplicar busca + filtros. */
  filteredCount: number;
  isLoading: boolean;
  isFetching: boolean;
  forceRefresh: () => void;
  /** Resolve freelancers por id (server-side). */
  getFreelancersByIds: (ids: string[]) => Promise<FreelancerListItem[]>;
}

/**
 * Fonte de dados da listagem de freelancers — SERVER-SIDE (RPC search_freelancers):
 * paginação/busca/filtro/ordenação no Postgres, sem cache client-side. O antigo
 * modo "Completo (offline)" (FreelancersCacheContext) foi aposentado.
 */
export function useFreelancersData({
  searchTerm,
  searchOnlyName,
  filters,
  sortOption,
  page,
  pageSize,
}: UseFreelancersDataParams): FreelancersDataResult {
  const serverQuery = useQuery({
    queryKey: [
      "freelancers-search",
      { searchTerm, searchOnlyName, filters, sortOption, page, pageSize },
    ],
    queryFn: async () => {
      const rpcParams = {
        p_search: searchTerm.trim() ? searchTerm.trim() : null,
        p_search_only_name: searchOnlyName,
        p_filters: filters,
        p_sort: sortOption,
      };
      // RPC criada na migração 108 — tipos gerados do Supabase ainda não a conhecem.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc("search_freelancers", {
        ...rpcParams,
        p_limit: pageSize,
        p_offset: (page - 1) * pageSize,
      });
      if (error) throw error;
      const rows = (data ?? []) as Array<
        FreelancerListItem & { total_count: number }
      >;
      let filteredCount = rows.length > 0 ? Number(rows[0].total_count) : 0;
      // Página fora do range retorna 0 linhas e esconderia o total_count real.
      if (rows.length === 0 && page > 1) {
        const { data: firstRow, error: countError } = await (
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          supabase as any
        ).rpc("search_freelancers", { ...rpcParams, p_limit: 1, p_offset: 0 });
        if (countError) throw countError;
        const countRows = (firstRow ?? []) as Array<{ total_count: number }>;
        filteredCount =
          countRows.length > 0 ? Number(countRows[0].total_count) : 0;
      }
      return {
        items: rows.map((r) => r as FreelancerListItem),
        filteredCount,
      };
    },
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  // Total cadastrado (todos) — contagem leve via HEAD.
  const totalCountQuery = useQuery({
    queryKey: ["freelancers-total-count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("freelancers")
        .select("id", { count: "exact", head: true });
      if (error) throw error;
      return count ?? 0;
    },
    staleTime: 5 * 60_000,
  });

  const getFreelancersByIds = useCallback(
    async (ids: string[]): Promise<FreelancerListItem[]> => {
      if (ids.length === 0) return [];
      const { data, error } = await supabase
        .from("freelancers")
        .select(BY_IDS_COLUMNS)
        .in("id", ids);
      if (error) throw error;
      return (data ?? []) as unknown as FreelancerListItem[];
    },
    [],
  );

  return {
    pageItems: serverQuery.data?.items ?? EMPTY,
    totalCount: totalCountQuery.data ?? 0,
    filteredCount: serverQuery.data?.filteredCount ?? 0,
    isLoading: serverQuery.isLoading,
    isFetching: serverQuery.isFetching,
    forceRefresh: () => {
      serverQuery.refetch();
      totalCountQuery.refetch();
    },
    getFreelancersByIds,
  };
}
